import { Logger } from 'dreadcabinet';
import * as Storage from "../util/storage";

// Sanitize a file extension to prevent glob injection.
// Only allows alphanumeric characters, stripping any glob metacharacters.
const sanitizeExtension = (ext: string): string => {
    return ext.replace(/[^a-zA-Z0-9]/g, '');
};

// Process files with unstructured input pattern
export const process = async (
    inputDirectory: string,
    recursive: boolean,
    extensions: string[],
    limit: number | undefined,
    logger: Logger,
    callback: (file: string) => Promise<void>,
    concurrency?: number
): Promise<number> => {
    const storage = Storage.create({ log: logger.debug });

    let fileCount = 0;
    let filePattern = `${recursive ? '**/' : ''}*`;

    if (extensions && extensions.length > 0) {
        const safeExtensions = extensions.map(sanitizeExtension).filter(ext => ext.length > 0);
        if (safeExtensions.length === 0) {
            throw new Error('No valid extensions provided after sanitization.');
        }
        // Ensure the pattern correctly handles extensions with or without recursion
        if (recursive) {
            filePattern = `**/*.{${safeExtensions.join(',')}}`;
        } else {
            filePattern = `*.{${safeExtensions.join(',')}}`;
        }
        logger.debug(`Applying extension filter: ${safeExtensions.join(',')}`);
    } else if (!recursive) {
        // Non-recursive without extension filter: only files in the top directory
        filePattern = `*.*`; // Adjust if files without extensions need matching
    }

    logger.info('Processing unstructured files %s in %s with pattern %s',
        recursive ? 'recursively' : 'non-recursively', inputDirectory, filePattern);

    await storage.forEachFileIn(inputDirectory, async (file: string) => {
        try {
            logger.debug('Processing file %s', file);
            // Call callback without date for unstructured input
            await callback(file); // Pass undefined for the date parameter
            fileCount++;
        } catch (error) {
            if (error instanceof Error) {
                logger.error('Error processing file %s: %s\n%s', file, error.message, error.stack);
            } else {
                logger.error('Error processing file %s: %s', file, error);
            }
        }
    }, { pattern: filePattern, limit, concurrency } as { pattern: string, limit?: number, concurrency?: number });

    return fileCount;
};

